import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";
import OutstandingGrid from "./OutstandingGrid";
import InstrumentTypeFields from "./InstrumentTypeFields";
import { buildPaymentHeads } from "./feeTax";

interface SchoolOption {
  id: number;
  name: string;
}

interface CollectPaymentModalProps {
  show: boolean;
  onHide: () => void;
  enrollmentId: number;
  onSuccess: () => void;
  schools?: SchoolOption[];
  defaultSchoolId?: number | null;
}

const CollectPaymentModal: React.FC<CollectPaymentModalProps> = ({
  show,
  onHide,
  enrollmentId,
  onSuccess,
  schools: schoolsProp,
  defaultSchoolId,
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [installments, setInstallments] = useState<any[]>([]);
  const [amounts, setAmounts] = useState<Record<string, number>>({});

  // Form fields
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [instrumentTypeId, setInstrumentTypeId] = useState<number | null>(null);
  const [bankName, setBankName] = useState("");
  const [bankBranchName, setBankBranchName] = useState("");
  const [instrumentDate, setInstrumentDate] = useState("");
  const [instrumentIdentifier, setInstrumentIdentifier] = useState("");
  const [remarks, setRemarks] = useState("");
  const [statusId, setStatusId] = useState(1); // 1=Active, 3=Cleared
  const [schoolId, setSchoolId] = useState<number | null>(null);

  const schools = schoolsProp || [];

  useEffect(() => {
    if (show) {
      setError(null);
      setSuccessMsg(null);
      setAmounts({});
      setSchoolId(defaultSchoolId ?? (schools.length === 1 ? schools[0].id : null));
      setLoading(true);
      EnrollmentService.getOutstanding(enrollmentId)
        .then((res: any) => {
          if (res.data?.success) {
            setInstallments(res.data.data.installments || []);
          } else {
            setError(res.data?.message || "Failed to load outstanding");
          }
        })
        .catch((err: any) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [show, enrollmentId]);

  const handleAmountChange = (
    installmentId: number,
    headId: number,
    amount: number,
  ) => {
    setAmounts((prev) => ({
      ...prev,
      [`${installmentId}-${headId}`]: amount,
    }));
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccessMsg(null);

    if (!paymentDate) {
      setError("Payment date is required");
      return;
    }
    if (!instrumentTypeId) {
      setError("Select an instrument type");
      return;
    }

    // Build heads array from amounts. `amount` is the BASE slice and
    // `taxAmount` the carved-out tax; the backend records both verbatim
    // rather than re-deriving tax from the gross the cashier typed.
    const heads = buildPaymentHeads(amounts, installments);

    if (heads.length === 0) {
      setError("Enter at least one payment amount");
      return;
    }

    setSubmitting(true);
    try {
      const res = await EnrollmentService.collectPayment(enrollmentId, {
        paymentDate,
        instrumentTypeId,
        bankName,
        bankBranchName,
        instrumentDate: instrumentDate || null,
        instrumentIdentifier,
        remarks,
        heads,
        statusId,
        schoolId,
      });
      if (res.data?.success) {
        const { autoReceiptNumber, totalAmount } = res.data.data;
        setSuccessMsg(
          `Payment collected! Receipt: ${autoReceiptNumber}, Amount: ${totalAmount.toLocaleString()}`,
        );
        onSuccess();
      } else {
        setError(res.data?.message || "Failed to collect payment");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to collect payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Collect Payment</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13 }}>
        {error && <Alert variant="danger" style={{ fontSize: 12 }}>{error}</Alert>}
        {successMsg && (
          <Alert variant="success" style={{ fontSize: 12 }}>{successMsg}</Alert>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: 30 }}>
            <Spinner role="status" animation="border" size="sm" /> Loading outstanding...
          </div>
        ) : (
          <>
            <OutstandingGrid
              installments={installments}
              amounts={amounts}
              onAmountChange={handleAmountChange}
            />

            <hr style={{ margin: "12px 0" }} />

            <Form.Group className="mb-2">
              <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                Payment Date
              </Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </Form.Group>

            {schools.length > 0 && (
              <Form.Group className="mb-2">
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                  Paid At
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={schoolId || ""}
                  onChange={(e) =>
                    setSchoolId(Number(e.target.value) || null)
                  }
                >
                  <option value="">-- Select School --</option>
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <InstrumentTypeFields
              instrumentTypeId={instrumentTypeId}
              onInstrumentTypeChange={setInstrumentTypeId}
              bankName={bankName}
              onBankNameChange={setBankName}
              bankBranchName={bankBranchName}
              onBankBranchNameChange={setBankBranchName}
              instrumentDate={instrumentDate}
              onInstrumentDateChange={setInstrumentDate}
              instrumentIdentifier={instrumentIdentifier}
              onInstrumentIdentifierChange={setInstrumentIdentifier}
            />

            <Form.Group className="mb-2">
              <Form.Label style={{ fontSize: 12 }}>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>
                Payment Status
              </Form.Label>
              <Form.Control
                as="select"
                size="sm"
                value={statusId}
                onChange={(e) => setStatusId(Number(e.target.value))}
              >
                <option value={1}>Active (Pending Clearance)</option>
                <option value={3}>Cleared</option>
              </Form.Control>
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {successMsg ? (
          <Button variant="secondary" size="sm" onClick={onHide}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onHide}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={submitting || loading}
            >
              {submitting ? (
                <>
                  <Spinner role="status" animation="border" size="sm" /> Saving...
                </>
              ) : (
                "Collect Payment"
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default CollectPaymentModal;
